/**
 * The Debit Request shapes the API actually returns, and the labels both screens share.
 *
 * Held apart from the two screens because the loan panel and the portfolio draft queue read the
 * same rows and must name a payer, a purpose and a status identically: a status styled one way on
 * the loan and another in the queue is the kind of drift that makes a servicer distrust both.
 */

/** Who the money is pulled from. */
export type DebitPayer = 'borrower' | 'participant'

/** What it is collected for. Drives the bank statement descriptor and the ledger category. */
export type DebitPurpose = 'downpayment' | 'loanFunding' | 'drawFunding' | 'other'

export type DebitStatus = 'pending' | 'drafted' | 'settled' | 'returned' | 'canceled'

/**
 * Which record the banking resolved off. `request` is a one-time authorization typed on the
 * request itself, which wins over the payer's stored details; `none` means there is nothing to
 * debit, which is what blocks a draft.
 */
export type DebitBankingSource = 'request' | 'loanAch' | 'participant' | 'none'

/**
 * One request as the API hands it back.
 *
 * Note what is absent: no `routingNumber` and no `accountNumber`. The API never returns an account
 * number in full, only `accountNumberLast4` alongside `hasOwnBanking`, so a form cannot round-trip
 * stored banking and must offer to replace it instead of pre-filling it.
 */
export interface DebitRequestRow {
  id: number
  loanNumber: number
  payer: DebitPayer
  purpose: DebitPurpose
  /** The backend's own wording for the purpose, reused rather than re-derived here. */
  purposeLabel: string
  status: DebitStatus
  amount: number
  dueDate: string
  participantUserId: number | null
  payerName: string
  drawId: number | null
  participationFundingId: number | null
  bankName: string
  accountType: string
  secCode: string
  /** True when a one-time authorization is on the request: BOTH routing and account were given. */
  hasOwnBanking: boolean
  accountNumberLast4: string
  /** The ACH run that drafted it. Null until drafted, and cleared if that run is reversed. */
  achRunId: number | null
  referenceNumber: string
  postToTrustLedger: boolean
  trustAccountId: number | null
  trustLedgerId: number | null
  settledDate: string
  memo: string
  createdBy: string
  createdAt: string
}

/** One row of the draft preview: the banking it resolved to and whether it can be drafted. */
export interface DebitPreviewItem {
  id: number
  loanNumber: number
  payer: DebitPayer
  purpose: DebitPurpose
  purposeLabel: string
  payerName: string
  amount: number
  bankName: string
  accountNumberLast4: string
  bankingSource: DebitBankingSource
  secCode: string
  achReady: boolean
  /** Empty when ready; otherwise a sentence saying what is wrong. */
  achBlockedReason: string
}

export interface DebitPreview {
  items: DebitPreviewItem[]
  readyCount: number
  readyTotal: number
  blockedCount: number
}

/** What a draft did. `skipped` carries anyone whose banking was not ready after all. */
export interface DebitDraftResult {
  runId: number
  count: number
  total: number
  secCode: string
  descriptor: string
  processed: Array<{ id: number; loanNumber: number; name: string; amount: number }>
  skipped: Array<{ name: string; reason: string }>
}

/** An investor on the loan, as Servicing > Investors lists them. */
export interface LoanParticipantOption {
  participantUserId: number
  participantName: string
}

export const PAYER_LABELS: Record<DebitPayer, string> = {
  borrower: 'Borrower',
  participant: 'Investor',
}

export const PURPOSE_LABELS: Record<DebitPurpose, string> = {
  downpayment: 'Borrower downpayment',
  loanFunding: 'Investor loan funding',
  drawFunding: 'Investor draw funding',
  other: 'Collection',
}

/**
 * The purposes offered for each payer. A downpayment is the borrower's cash to close; funding a
 * loan or a draw is an investor putting their money in. `other` is open to both, because a
 * servicer collecting something the list does not name should not have to mislabel it.
 */
export const PURPOSES_FOR_PAYER: Record<DebitPayer, DebitPurpose[]> = {
  borrower: ['downpayment', 'other'],
  participant: ['loanFunding', 'drawFunding', 'other'],
}

export const STATUS_LABELS: Record<DebitStatus, string> = {
  pending: 'Pending',
  drafted: 'Drafted',
  settled: 'Settled',
  returned: 'Returned',
  canceled: 'Canceled',
}

export const STATUS_STYLE: Record<DebitStatus, string> = {
  pending: 'bg-amber-50 text-amber-800',
  drafted: 'bg-blue-50 text-blue-800',
  settled: 'bg-green-50 text-green-800',
  returned: 'bg-red-50 text-red-800',
  canceled: 'bg-slate-100 text-slate-500 line-through',
}

/** How the banking a request would debit reads on screen, by where it came from. */
export const BANKING_SOURCE_LABELS: Record<DebitBankingSource, string> = {
  request: 'One-time authorization',
  loanAch: "The loan's ACH record",
  participant: "The investor's record",
  none: 'None on file',
}

/** SEC codes offered on the form. Left blank, the backend defaults: PPD a borrower, CCD an investor. */
export const SEC_CODE_OPTIONS = ['PPD', 'CCD', 'WEB', 'TEL']

/**
 * The banking on a request, in one line: which record it will debit and the last four digits, or
 * a plain statement that there is none. Deliberately never shows a full account number, because
 * the API does not return one.
 */
export const describeBanking = (row: Pick<DebitRequestRow, 'hasOwnBanking' | 'bankName' | 'accountNumberLast4'>) => {
  if (!row.hasOwnBanking) return row.accountNumberLast4 ? `Stored - ${row.accountNumberLast4}` : ''
  const bank = row.bankName || 'One-time'
  return row.accountNumberLast4 ? `${bank} - ${row.accountNumberLast4}` : bank
}
